// frontend/src/components/PerformanceDashboard.jsx
// ✅ API path includes /creator prefix
// ✅ Stacked usage bar: image vs video uses in distinct colors
// ✅ Emojis removed for a cleaner design
import React from "react";
import { getIdentityHeaders, waitForIdentity } from "../lib/identity";
import { API_BASE } from "../lib/api";

const api = (path) => (API_BASE ? `${API_BASE}${path}` : path);

// Image = purple, Video = teal (matches EarningsAnalytics)
const COLOR_IMAGE = "#6D5EF9";
const COLOR_VIDEO = "#14B8A6";

export default function PerformanceDashboard() {
  const [loading, setLoading] = React.useState(true);
  const [metrics, setMetrics] = React.useState([]);
  const [period, setPeriod] = React.useState({ days: 30, startDate: null, endDate: null });
  const [selectedDays, setSelectedDays] = React.useState(30);

  React.useEffect(() => {
    loadMetrics();
  }, [selectedDays]);

  async function loadMetrics() {
    setLoading(true);
    try {
      await waitForIdentity();

      const res = await fetch(
        api(`/api/creator/analytics/my-styles/performance?days=${selectedDays}`),
        {
          credentials: "include",
          headers: getIdentityHeaders(),
        }
      );

      if (!res.ok) throw new Error("Failed to load metrics");

      const data = await res.json();
      setMetrics(data.metrics || []);
      setPeriod(data.period || { days: selectedDays, startDate: null, endDate: null });
    } catch (err) {
      console.error("[PerformanceDashboard] Error:", err);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="perf-loading">
        <div className="spinner"></div>
        <p>Loading analytics...</p>
      </div>
    );
  }

  if (metrics.length === 0) {
    return (
      <div className="perf-empty">
        <p>No performance data available yet. Create some styles and they'll appear here.</p>
      </div>
    );
  }

  const maxUses = Math.max(...metrics.map((m) => m.totalUses), 1);

  return (
    <div className="perf-dashboard">
      <div className="perf-header">
        <h2>Styles Performance</h2>
        <select
          className="perf-time-filter"
          value={selectedDays}
          onChange={(e) => setSelectedDays(Number(e.target.value))}
        >
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
        </select>
      </div>

      {/* Legend */}
      <div className="perf-legend">
        <div className="perf-legend-item">
          <span className="perf-legend-swatch" style={{ background: COLOR_IMAGE }} />
          <span className="perf-legend-label">Image uses</span>
        </div>
        <div className="perf-legend-item">
          <span className="perf-legend-swatch" style={{ background: COLOR_VIDEO }} />
          <span className="perf-legend-label">Video uses</span>
        </div>
      </div>

      <div className="perf-grid">
        {metrics.map((metric, index) => (
          <StyleCard key={metric.styleId} metric={metric} rank={index + 1} maxUses={maxUses} />
        ))}
      </div>
    </div>
  );
}

function StyleCard({ metric, rank, maxUses }) {
  const getTrendText = () => {
    const change = metric.usesChange;
    if (change > 0) return `Up ${change}%`;
    if (change < 0) return `Down ${Math.abs(change)}%`;
    return "No change";
  };

  const getTrendClass = () => {
    if (metric.trend === "up") return "trend-up";
    if (metric.trend === "down") return "trend-down";
    return "trend-flat";
  };

  const getQualityColor = () => {
    if (metric.qualityScore >= 80) return "#10B981";
    if (metric.qualityScore >= 60) return "#F59E0B";
    return "#9CA3AF";
  };

  const getQualityLabel = () => {
    if (metric.qualityScore >= 80) return "Excellent";
    if (metric.qualityScore >= 60) return "Good";
    return "Needs Work";
  };

  // Split uses: backend may provide imageUses/videoUses; fall back to totals.
  const totalUses = metric.totalUses || 0;
  const videoUses = Number(metric.videoUses ?? 0);
  const imageUses = Number(metric.imageUses ?? Math.max(0, totalUses - videoUses));
  const safeTotal = imageUses + videoUses || totalUses || 1;

  const trackWidth = (totalUses / maxUses) * 100;
  const imageShare = imageUses / safeTotal;
  const videoShare = videoUses / safeTotal;

  return (
    <div className="perf-card">
      <div className="perf-card-header">
        <div className="perf-card-title">
          <span className="perf-rank">{rank}</span>
          <h3>{metric.styleName}</h3>
        </div>
        <div className={`perf-trend ${getTrendClass()}`}>
          <span className="trend-text">{getTrendText()}</span>
        </div>
      </div>

      {/* Stacked usage bar */}
      <div className="perf-bar-container">
        <div className="perf-bar-track" style={{ width: `${Math.max(trackWidth, 6)}%` }}>
          {imageShare > 0 && (
            <div
              className="perf-bar-seg"
              style={{ background: COLOR_IMAGE, width: `${imageShare * 100}%` }}
              title={`Image uses: ${imageUses}`}
            />
          )}
          {videoShare > 0 && (
            <div
              className="perf-bar-seg"
              style={{ background: COLOR_VIDEO, width: `${videoShare * 100}%` }}
              title={`Video uses: ${videoUses}`}
            />
          )}
        </div>
        <span className="perf-bar-label">
          {totalUses} use{totalUses !== 1 ? "s" : ""}
          {videoUses > 0 && <span className="perf-bar-sublabel"> · {videoUses} video</span>}
        </span>
      </div>

      <div className="perf-conversion">
        <span className="conversion-label">
          {metric.totalViews.toLocaleString()} views to {totalUses} uses
        </span>
        <span className="conversion-rate">{metric.conversionRate}% conversion</span>
      </div>

      <div className="perf-metrics">
        <div className="metric">
          <span className="metric-label">Score</span>
          <span className="metric-value" style={{ color: getQualityColor() }}>
            {metric.qualityScore}
          </span>
        </div>
        <div className="metric">
          <span className="metric-label">Likes</span>
          <span className="metric-value">{metric.likes}</span>
        </div>
        <div className="metric">
          <span className="metric-label">Reports</span>
          <span className="metric-value">{metric.reports}</span>
        </div>
      </div>

      <div className="perf-card-footer">
        {metric.lastUsed ? (
          <span className="last-used">Last used: {formatTimeAgo(metric.lastUsed)}</span>
        ) : (
          <span className="last-used">Never used</span>
        )}
        <span
          className="quality-badge"
          style={{
            background: metric.qualityScore < 60 ? "#F3F0FF" : getQualityColor(),
            color: metric.qualityScore < 60 ? "#6D5EF9" : "#fff",
            border: metric.qualityScore < 60 ? "1px solid #E7E2FF" : "none",
          }}
        >
          {getQualityLabel()}
        </span>
      </div>
    </div>
  );
}

function formatTimeAgo(date) {
  const now = new Date();
  const past = new Date(date);
  const diffMs = now - past;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return "1d ago";
  return `${diffDays}d ago`;
}

const styles = `
.perf-dashboard{ width:100%; }

.perf-header{
  display:flex;
  justify-content:space-between;
  align-items:center;
  margin-bottom:18px;
}

.perf-header h2{
  font-size:24px;
  font-weight:800;
  color:#111827;
  margin:0;
}

.perf-time-filter{
  padding:8px 16px;
  border:1px solid #E5E7EB;
  border-radius:8px;
  font-size:14px;
  font-weight:600;
  background:#fff;
  cursor:pointer;
  transition: all 0.2s;
}

.perf-time-filter:hover{
  border-color:#6D5EF9;
  box-shadow:0 0 0 3px #F3F0FF;
}

.perf-legend{
  display:flex;
  gap:18px;
  margin-bottom:18px;
  flex-wrap:wrap;
}

.perf-legend-item{
  display:flex;
  align-items:center;
  gap:7px;
}

.perf-legend-swatch{
  width:14px;
  height:14px;
  border-radius:4px;
  display:inline-block;
}

.perf-legend-label{
  font-size:13px;
  font-weight:700;
  color:#6B7280;
}

.perf-grid{
  display:grid;
  gap:16px;
}

.perf-card{
  background:#fff;
  border:1px solid #E5E7EB;
  border-radius:12px;
  padding:20px;
  transition: all 0.2s;
}

.perf-card:hover{
  box-shadow:0 4px 12px rgba(0,0,0,0.08);
  transform:translateY(-2px);
}

.perf-card-header{
  display:flex;
  justify-content:space-between;
  align-items:center;
  margin-bottom:12px;
}

.perf-card-title{
  display:flex;
  align-items:center;
  gap:10px;
}

.perf-rank{
  font-size:14px;
  font-weight:800;
  color:#9CA3AF;
  min-width:18px;
}

.perf-card-title h3{
  font-size:18px;
  font-weight:700;
  color:#111827;
  margin:0;
}

.perf-trend{
  display:flex;
  align-items:center;
  font-size:13px;
  font-weight:700;
  padding:4px 10px;
  border-radius:8px;
}

.perf-trend.trend-up{ background:#D1FAE5; color:#065F46; }
.perf-trend.trend-down{ background:#FEE2E2; color:#991B1B; }
.perf-trend.trend-flat{ background:#F3F4F6; color:#6B7280; }

.perf-bar-container{
  position:relative;
  height:32px;
  background:#F3F4F6;
  border-radius:8px;
  overflow:hidden;
  margin-bottom:12px;
}

.perf-bar-track{
  height:100%;
  display:flex;
  border-radius:8px;
  overflow:hidden;
  transition: width 0.6s ease;
}

.perf-bar-seg{
  height:100%;
  transition: width 0.6s ease;
}

.perf-bar-label{
  position:absolute;
  left:12px;
  top:50%;
  transform:translateY(-50%);
  font-size:14px;
  font-weight:700;
  color:#111827;
  pointer-events:none;
}

.perf-bar-sublabel{
  font-weight:600;
  color:#374151;
}

.perf-conversion{
  display:flex;
  justify-content:space-between;
  align-items:center;
  font-size:13px;
  margin-bottom:12px;
  padding:8px 0;
  border-bottom:1px solid #F3F4F6;
}

.conversion-label{ color:#6B7280; }
.conversion-rate{ font-weight:700; color:#111827; }

.perf-metrics{
  display:grid;
  grid-template-columns:repeat(3, 1fr);
  gap:12px;
  margin-bottom:16px;
  padding:16px 0;
  border-top:1px solid #F3F4F6;
  border-bottom:1px solid #F3F4F6;
}

.metric{
  display:flex;
  flex-direction:column;
  align-items:center;
  gap:4px;
}

.metric-label{
  font-size:11px;
  color:#9CA3AF;
  text-transform:uppercase;
  font-weight:600;
}

.metric-value{
  font-size:16px;
  font-weight:800;
  color:#111827;
}

.perf-card-footer{
  display:flex;
  justify-content:space-between;
  align-items:center;
}

.last-used{
  font-size:12px;
  color:#6B7280;
}

.quality-badge{
  padding:4px 12px;
  border-radius:999px;
  font-size:11px;
  font-weight:800;
  text-transform:uppercase;
}

.perf-loading, .perf-empty{
  text-align:center;
  padding:48px 20px;
  color:#6B7280;
}

.spinner{
  width:40px;
  height:40px;
  border:4px solid #F3F4F6;
  border-top-color:#6D5EF9;
  border-radius:50%;
  animation:spin 0.8s linear infinite;
  margin:0 auto 16px;
}

@keyframes spin{ to{ transform:rotate(360deg); } }

@media (max-width:768px){
  .perf-header{
    flex-direction:column;
    gap:12px;
    align-items:flex-start;
  }
  .perf-time-filter{ width:100%; }
}
`;

if (typeof document !== "undefined") {
  const styleElement = document.createElement("style");
  styleElement.textContent = styles;
  document.head.appendChild(styleElement);
}